import type { ReactNode } from 'react'
import { ClockIcon, Lock01Icon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import useStepRouter from 'modules/course/hooks/use-step-router'
import type {
  CourseMenuLectureInterface,
  CourseUnavailabilityReason,
} from 'modules/course/types/course-menu-interface'
import { addPublicCustomClassNamePrefix } from 'modules/course/utils/add-class-prefix'

interface CourseAvailabilityIconProps {
  unavailabilityReason: CourseUnavailabilityReason
  lectureId?: CourseMenuLectureInterface['id']
}

export default function CourseAvailabilityIcon({
  unavailabilityReason,
  lectureId,
}: CourseAvailabilityIconProps): ReactNode {
  const { t } = useTranslation()
  const { getLectureUrl } = useStepRouter()

  const isDripping = unavailabilityReason === 'dripping_access_is_not_granted_yet'
  const isLocked =
    unavailabilityReason === 'not_included_in_partial_access' ||
    unavailabilityReason === 'mandatory_assignment_not_completed' ||
    unavailabilityReason === 'not_enrolled' ||
    unavailabilityReason === 'purchase_required'

  return (
    <span
      title={t(
        isDripping
          ? 'course.menu.module.dripping_access_unavailable.tooltip'
          : 'course.menu.module.partial_access_unavailable.tooltip',
      )}
    >
      {isDripping ? (
        <ClockIcon
          className={'h-[17px] text-THEME-disabled'}
          data-test-id={
            lectureId
              ? `course-menu-lecture-${lectureId}-clock-icon`
              : 'course-menu-lecture-clock-icon'
          }
        />
      ) : isLocked && lectureId ? (
        <LinkWithoutPrefetch
          className={addPublicCustomClassNamePrefix('course-modules-list__module-sales-page-link')}
          href={getLectureUrl(lectureId)}
          scroll={false}
          data-test-id={`course-menu-lecture-${lectureId}-lock-icon`}
        >
          <Lock01Icon className={'h-[15px] text-THEME-disabled'} />
        </LinkWithoutPrefetch>
      ) : (
        <span
          className={addPublicCustomClassNamePrefix('course-modules-list__module-sales-page-link')}
          data-test-id={'course-menu-lecture-lock-icon'}
        >
          <Lock01Icon className={'h-[15px] text-THEME-disabled'} />
        </span>
      )}
    </span>
  )
}
